import { Text, Grid, useTheme } from '@simplywallst/ui-core';

import GoIcon from '@assets/images/watchlist/go.svg';
import HoldIcon from '@assets/images/watchlist/hold.svg';

import { isNil, formatterPercentage } from '@/utilities/format';

type Company = {
  analysisValue: {
    lastSharePrice: number | null;
    npvPerShare: number | null;
  };
};

interface Props {
  company: Company;
  fairValue?: number | null;
}

export const calculateIntrinsicDiscount = (
  company: Company,
  companyFairValue?: number | null
) => {
  const sharePrice = company.analysisValue.lastSharePrice;
  const fairValue = companyFairValue ?? company.analysisValue.npvPerShare;

  if (isNil(sharePrice) || isNil(fairValue) || fairValue === 0) {
    return null;
  }

  return (fairValue - sharePrice) / fairValue;
};

const formatDiscount = (discount: number | null) => {
  const discountAbs = discount === null ? null : Math.abs(discount);

  return formatterPercentage(discountAbs);
};

export const CompanyIntrinsicDiscount = ({ company, fairValue }: Props) => {
  const theme = useTheme();
  const discount = calculateIntrinsicDiscount(company, fairValue);

  if (discount === null) {
    return (
      <Text
        typography="tinyCondensed"
        color="whiteRGBA"
        opacity={0.5}
        isTruncated
      >
        {formatDiscount(discount)}
      </Text>
    );
  }

  if (discount <= 0) {
    return (
      <Grid
        data-cy-id="intrinsic-discount"
        templateColumns="12px auto"
        alignItems="center"
        gap="2px"
      >
        <HoldIcon fill={theme.color.badRGBA(0.7)} role="presentation" />
        <Text
          typography="tinyCondensed"
          color="whiteRGBA"
          opacity={0.5}
          isTruncated
        >
          {formatDiscount(discount)} OVERVALUED
        </Text>
      </Grid>
    );
  }

  return (
    <Grid
      data-cy-id="intrinsic-discount"
      templateColumns="12px auto"
      alignItems="center"
      gap="2px"
    >
      <GoIcon fill={theme.color.goodRGBA(0.7)} role="presentation" />
      <Text
        typography="tinyCondensed"
        color="whiteRGBA"
        opacity={0.5}
        isTruncated
      >
        {formatDiscount(discount)} UNDERVALUED
      </Text>
    </Grid>
  );
};
